/**
 * DataTable — DS-20…25, the single most important element in a system like this.
 *
 * Built on TanStack Table v8 in **manual** mode: paging and sorting are server
 * concerns here, so the component never slices or re-orders `data`; it reports
 * what the user asked for and renders exactly what the API returned.
 */
import { forwardRef, useMemo } from 'react';
import type { ForwardedRef, HTMLAttributes, ReactElement, ReactNode, RefAttributes } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type {
  Cell,
  ColumnDef,
  OnChangeFn,
  Row,
  RowData,
  SortingState,
  Table,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Inbox } from 'lucide-react';
import { DEFAULT_PAGE_SIZE } from '@nova/shared';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../lib/responsive';
import { Checkbox } from './Checkbox';
import { Pagination } from './Pagination';

/**
 * NFR-05 — where a column lands in the card a phone renders instead of a row.
 *
 * `title` is the headline, `meta` joins the subdued line under it, `row` is a
 * label/value pair in the body, `actions` is the trailing control strip and
 * `hidden` drops the column on a phone entirely. Everything is inferred when a
 * screen says nothing: the first real column becomes the title, a column with
 * no header becomes actions, and the rest become rows — so a table written
 * before this existed still reads correctly on a phone.
 */
export type MobileColumnRole = 'title' | 'meta' | 'row' | 'actions' | 'hidden';

declare module '@tanstack/react-table' {
  // The generics must mirror the upstream declaration for the merge to apply.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** DS-23 — end-aligned, mono, bold; a negative value turns red (DS-24). */
    numeric?: boolean;
    /** Fixed column width, e.g. `'120px'`. */
    width?: string;
    /** Extra classes merged onto every body cell of this column. */
    cellClassName?: string;
    /** Extra classes merged onto this column's header cell. */
    headerClassName?: string;
    /** NFR-05 — where this column lands in the phone's card. Inferred if omitted. */
    mobile?: MobileColumnRole;
  }
}

const SELECT_COLUMN_ID = '__select';
const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-full', 'w-2/3', 'w-5/6'];

export interface DataTableProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The value generic is left open on purpose: `createColumnHelper<T>()` emits a
   * different `TValue` per column, and pinning it to `unknown` would reject every
   * helper-built column set. This is TanStack's own signature for a generic wrapper.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  data: T[];

  /** Shimmer rows instead of a blank flash — the header and layout never jump. */
  isLoading?: boolean;
  /**
   * A refetch running behind rows that are already on screen — changing a filter,
   * a page or a sort on a list that keeps its previous data. Ignored while
   * `isLoading` is true, since the skeleton already says the same thing. The rows
   * stay readable and clickable; they only dim under an indeterminate bar, because
   * blanking a table the user is reading is worse than showing it a beat stale.
   */
  isFetching?: boolean;

  // --- server-side pagination; pass `total` + `onPageChange` to get the strip ---
  total?: number;
  /** 1-based. */
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  // --- server-side sorting; `field` is the column's id ---
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (field: string, dir: 'asc' | 'desc') => void;

  onRowClick?: (row: T) => void;
  /** DS-24 — semantic row tinting, e.g. `bg-emerald-50/40` for a completed row. */
  rowClassName?: (row: T) => string | undefined;

  emptyMessage?: string;
  /** DS-25 — default true. */
  stickyHeader?: boolean;
  /** Desktop density (`p-2.5`) by default; `false` gives the tablet `p-3` (DS-54). */
  dense?: boolean;

  // --- multi-select (APT-09: multi-select and bulk close) ---
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Row identity for selection. Defaults to `row.id`, falling back to the index. */
  getRowId?: (row: T, index: number) => string;
  selectionLabel?: string;
  clearSelectionLabel?: string;

  /** Filters, search and actions. Rendered end-aligned above the table. */
  toolbar?: ReactNode;
  /** A totals row under the body: blue-600, mono, bold. */
  footerSummary?: ReactNode;

  /**
   * NFR-05 — below 1024px each row is rendered as a stacked card instead of a
   * table row, because nine columns on a 375px screen is a sideways scroll
   * nobody performs. Set `false` where the grid *is* the information — a
   * stocktake sheet, a matrix — and the table will scroll horizontally instead.
   */
  mobileCards?: boolean;
  /** Replaces the generated card outright when a screen wants its own. */
  renderMobileCard?: (row: T) => ReactNode;
}

/** DS-24 — a negative amount is red wherever it lands. */
function isNegative(value: unknown): boolean {
  if (typeof value === 'number') return value < 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed < 0 : value.trim().startsWith('-');
  }
  return false;
}

function DataTableImpl<T>(props: DataTableProps<T>, ref: ForwardedRef<HTMLDivElement>): ReactElement {
  const {
    columns,
    data,
    isLoading = false,
    isFetching = false,
    total,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    onPageChange,
    onPageSizeChange,
    sortBy,
    sortDir = 'asc',
    onSortChange,
    onRowClick,
    rowClassName,
    emptyMessage: emptyMessageProp,
    stickyHeader = true,
    dense = true,
    selectable = false,
    selectedIds,
    onSelectionChange,
    getRowId,
    selectionLabel: selectionLabelProp,
    clearSelectionLabel: clearSelectionLabelProp,
    toolbar,
    footerSummary,
    mobileCards = true,
    renderMobileCard,
    className,
    ...rest
  } = props;

  const { t } = useTranslation('common');
  const emptyMessage = emptyMessageProp ?? t('status.empty');
  const selectionLabel = selectionLabelProp ?? t('ui.table.selected');
  const clearSelectionLabel = clearSelectionLabelProp ?? t('ui.table.clearSelection');

  const selection = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const cellPadding = dense ? undefined : 'p-3';
  // NFR-05 — the same 1024px the stylesheet branches on, read from one place.
  const isMobile = useIsMobile();
  const cardMode = isMobile && mobileCards;

  const resolveRowId = useMemo(
    () =>
      getRowId ??
      ((row: T, index: number) => {
        const id = (row as { id?: unknown }).id;
        return typeof id === 'string' || typeof id === 'number' ? String(id) : String(index);
      }),
    [getRowId],
  );

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    const current = selectedIds ?? [];
    onSelectionChange(selection.has(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  // Select-all covers the page in view only — selections made on other pages survive
  // it, which is what a bulk close-out over a filtered list needs (APT-09).
  const togglePage = (pageIds: string[], selectAll: boolean) => {
    if (!onSelectionChange) return;
    const current = selectedIds ?? [];
    onSelectionChange(
      selectAll
        ? [...current, ...pageIds.filter((id) => !selection.has(id))]
        : current.filter((id) => !pageIds.includes(id)),
    );
  };

  const resolvedColumns = useMemo<DataTableProps<T>['columns']>(() => {
    if (!selectable) return columns;
    const selectColumn: ColumnDef<T> = {
      id: SELECT_COLUMN_ID,
      enableSorting: false,
      meta: { width: '2.5rem' },
      header: ({ table }) => {
        const pageIds = table.getRowModel().rows.map((row) => row.id);
        const selectedOnPage = pageIds.filter((id) => selection.has(id)).length;
        return (
          <Checkbox
            checked={pageIds.length > 0 && selectedOnPage === pageIds.length}
            indeterminate={selectedOnPage > 0 && selectedOnPage < pageIds.length}
            onChange={(next) => togglePage(pageIds, next)}
            aria-label={t('a11y.selectAllRows')}
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={selection.has(row.id)}
          onChange={() => toggleRow(row.id)}
          aria-label={t('a11y.selectRow')}
        />
      ),
    };
    return [selectColumn, ...columns];
    // `selection` identity changes with every toggle, which is exactly when the
    // checkbox renderers need rebuilding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, selectable, selection, selectedIds, onSelectionChange]);

  const sorting = useMemo<SortingState>(
    () => (sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : []),
    [sortBy, sortDir],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (!onSortChange) return;
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) return;
    onSortChange(first.id, first.desc ? 'desc' : 'asc');
  };

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => resolveRowId(row, index),
    manualPagination: true,
    manualSorting: true,
    // A column always sorts ascending first and never cycles back to "unsorted":
    // the API contract is (field, dir), it has no third state.
    sortDescFirst: false,
    enableSortingRemoval: false,
    enableMultiSort: false,
    state: { sorting },
    onSortingChange: handleSortingChange,
  });

  const rows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();
  const columnCount = leafColumns.length;
  const hasColumnFooters = leafColumns.some((column) => column.columnDef.footer !== undefined);
  const selectedCount = selection.size;
  const skeletonRows = Math.min(Math.max(pageSize, 3), 10);
  const showPagination = total !== undefined && onPageChange !== undefined;
  const isRefreshing = isFetching && !isLoading;

  // --- the phone's card, built from the same columns the table renders -------

  /** Column id → its header, rendered. The card uses it as the row's label. */
  const headerLabels = new Map<string, ReactNode>();
  for (const group of table.getHeaderGroups()) {
    for (const header of group.headers) {
      if (header.isPlaceholder) continue;
      headerLabels.set(header.column.id, flexRender(header.column.columnDef.header, header.getContext()));
    }
  }

  /**
   * The first column that is not the checkbox and not explicitly placed becomes
   * the headline, unless a column asked to be. It is resolved once for the whole
   * table rather than per row, so every card is laid out the same way.
   */
  const titleColumnId =
    leafColumns.find((column) => column.columnDef.meta?.mobile === 'title')?.id ??
    leafColumns.find(
      (column) =>
        column.id !== SELECT_COLUMN_ID &&
        column.columnDef.meta?.mobile === undefined &&
        !isActionColumn(column.columnDef.header),
    )?.id;

  function roleOf(cell: Cell<T, unknown>): MobileColumnRole {
    const explicit = cell.column.columnDef.meta?.mobile;
    if (explicit) return explicit;
    if (cell.column.id === SELECT_COLUMN_ID) return 'hidden'; // drawn in the card header instead
    if (cell.column.id === titleColumnId) return 'title';
    if (isActionColumn(cell.column.columnDef.header)) return 'actions';
    return 'row';
  }

  return (
    <div
      ref={ref}
      aria-busy={isLoading || isFetching || undefined}
      className={cn('flex min-h-0 flex-col', className)}
      data-testid="data-table"
      {...rest}
    >
      {(toolbar || (selectable && selectedCount > 0)) && (
        <div
          className={cn(
            // NFR-05 — a filter row that cannot wrap pushes its last control off
            // the edge of a phone, and `justify-end` makes that last control the
            // one the user was reaching for. Below 1024px it wraps and reads
            // from the start edge instead.
            // Below 1024px the tab strip above a table is the thing pinned to
            // the top of the scroller (NFR-05). Two bars claiming `top-0` means
            // the lower one is painted over — the search box would be there and
            // unreachable — so this one travels with the page instead.
            'sticky top-0 z-20 flex shrink-0 items-center gap-2 border-b px-2 py-1.5 max-lg:static max-lg:flex-wrap',
            selectable && selectedCount > 0
              ? 'border-blue-200 bg-blue-50'
              : 'border-slate-200 bg-white',
          )}
        >
          {selectable && selectedCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-blue-700">
                {selectionLabel} <span className="ltr-num font-mono">{selectedCount}</span>
              </span>
              <button
                type="button"
                onClick={() => onSelectionChange?.([])}
                className="rounded px-1.5 py-0.5 text-[11px] font-bold text-blue-600 transition hover:bg-blue-100"
              >
                {clearSelectionLabel}
              </button>
            </div>
          )}
          <div className="flex flex-1 items-center justify-end gap-2 max-lg:w-full max-lg:flex-wrap max-lg:justify-start">
            {toolbar}
          </div>
        </div>
      )}

      {/* The bar sits outside the scroller so it stays pinned to the top of the
          table while the rows underneath it scroll. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {isRefreshing && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-0.5 overflow-hidden bg-blue-100">
            <div className="absolute top-0 h-full w-2/5 animate-progressSlide rounded-full bg-blue-500" />
          </div>
        )}

        {/* DS-01 — the overflow lives here, never on the page. */}
        <div className="min-h-0 flex-1 overflow-auto border-b border-slate-200 bg-white">
          {cardMode ? (
            <MobileRows
              rows={rows}
              isLoading={isLoading}
              skeletonRows={skeletonRows}
              emptyMessage={emptyMessage}
              selectable={selectable}
              selection={selection}
              onToggleRow={toggleRow}
              onRowClick={onRowClick}
              rowClassName={rowClassName}
              headerLabels={headerLabels}
              roleOf={roleOf}
              renderMobileCard={renderMobileCard}
              footerSummary={
                !isLoading && rows.length > 0 && (hasColumnFooters || footerSummary) ? (
                  <MobileFooter
                    table={table}
                    headerLabels={headerLabels}
                    hasColumnFooters={hasColumnFooters}
                    footerSummary={footerSummary}
                  />
                ) : null
              }
            />
          ) : (
          <table className="table-base">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta;
                    const sortable = Boolean(onSortChange) && header.column.getCanSort();
                    const isSorted = sortBy === header.column.id;
                    const content = header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext());
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        scope="col"
                        style={meta?.width ? { width: meta.width } : undefined}
                        aria-sort={
                          sortable
                            ? isSorted
                              ? sortDir === 'desc'
                                ? 'descending'
                                : 'ascending'
                              : 'none'
                            : undefined
                        }
                        className={cn(
                          'th-base whitespace-nowrap',
                          cellPadding,
                          // DS-25 — the header stays put; the shadow keeps its rule
                          // visible once the collapsed border scrolls away.
                          // Same reason as the toolbar above — and in card mode
                          // there is no header row on a phone to begin with.
                          stickyHeader && 'sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.slate.200)] max-lg:static',
                          meta?.numeric && 'text-end',
                          meta?.headerClassName,
                        )}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className={cn(
                              'inline-flex items-center gap-1 rounded transition hover:text-slate-900',
                              isSorted && 'text-blue-700',
                            )}
                          >
                            {content}
                            {isSorted ? (
                              sortDir === 'desc' ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronUp className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 text-slate-300" />
                            )}
                          </button>
                        ) : (
                          content
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody className={cn('transition-opacity duration-150', isRefreshing && 'opacity-60')}>
              {isLoading &&
                Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`} className="border-b border-slate-100">
                    {leafColumns.map((column, columnIndex) => (
                      <td key={column.id} className={cn('td-base', cellPadding)}>
                        <div
                          className={cn(
                            'h-3 animate-pulse rounded bg-slate-200',
                            column.id === SELECT_COLUMN_ID
                              ? 'w-3.5'
                              : SKELETON_WIDTHS[(rowIndex + columnIndex) % SKELETON_WIDTHS.length],
                          )}
                        />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && rows.length === 0 && (
                <tr data-testid="table-empty">
                  <td colSpan={columnCount} className="p-8">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <Inbox className="h-8 w-8" strokeWidth={1.5} />
                      <span className="text-xs">{emptyMessage}</span>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading &&
                rows.map((row) => {
                  const isSelected = selectable && selection.has(row.id);
                  return (
                    <tr
                      key={row.id}
                      data-testid="table-row"
                      data-row-id={row.id}
                      data-selected={isSelected || undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      onKeyDown={
                        onRowClick
                          ? (event) => {
                              if (event.target !== event.currentTarget) return;
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick(row.original);
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'border-b border-slate-100 transition-colors hover:bg-slate-50',
                        onRowClick && 'cursor-pointer',
                        isSelected && 'bg-blue-50 hover:bg-blue-100',
                        rowClassName?.(row.original),
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta;
                        const numeric = meta?.numeric === true;
                        const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                        return (
                          <td
                            key={cell.id}
                            style={meta?.width ? { width: meta.width } : undefined}
                            onClick={
                              cell.column.id === SELECT_COLUMN_ID
                                ? (event) => event.stopPropagation()
                                : undefined
                            }
                            className={cn(
                              'td-base',
                              cellPadding,
                              // DS-23 / DS-24
                              numeric && 'whitespace-nowrap text-end font-mono font-bold',
                              numeric && isNegative(cell.getValue()) && 'text-red-500',
                              meta?.cellClassName,
                            )}
                          >
                            {/* DS-51 — the figure itself stays LTR inside the Arabic column. */}
                            {numeric ? <span className="ltr-num">{rendered}</span> : rendered}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>

            {/* Sticky lives on the footer cells, not on <tfoot> — browsers only honour it there.
                Totals over nothing are noise, so the footer follows the rows. */}
            {!isLoading && rows.length > 0 && (hasColumnFooters || footerSummary) && (
              <tfoot className={cn('transition-opacity duration-150', isRefreshing && 'opacity-60')}>
                {hasColumnFooters &&
                  table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => {
                        const meta = header.column.columnDef.meta;
                        return (
                          <td
                            key={header.id}
                            colSpan={header.colSpan}
                            className={cn(
                              'sticky bottom-0 z-10 border-t border-slate-200 bg-slate-50 p-2.5',
                              'font-mono font-bold text-blue-600',
                              cellPadding,
                              meta?.numeric && 'whitespace-nowrap text-end',
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.footer, header.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                {footerSummary && (
                  <tr>
                    {/* One spanning cell: lay the totals out with flex inside it. */}
                    <td
                      colSpan={columnCount}
                      className={cn(
                        'sticky bottom-0 z-10 border-t border-slate-200 bg-slate-50 p-2.5',
                        'font-mono font-bold text-blue-600',
                        cellPadding,
                      )}
                    >
                      {footerSummary}
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
          )}
        </div>
      </div>

      {showPagination && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NFR-05 — the phone's rendering of the same rows
// ---------------------------------------------------------------------------

/**
 * A column with no header is a control strip — the row's edit, print and delete
 * buttons — not a field. It has no label to put in a card, so it goes to the
 * bottom of one instead of pretending to be a value.
 */
function isActionColumn(header: unknown): boolean {
  return header === undefined || header === null || header === '';
}

interface MobileRowsProps<T> {
  rows: Row<T>[];
  isLoading: boolean;
  skeletonRows: number;
  emptyMessage: string;
  selectable: boolean;
  selection: Set<string>;
  onToggleRow: (id: string) => void;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  headerLabels: Map<string, ReactNode>;
  roleOf: (cell: Cell<T, unknown>) => MobileColumnRole;
  renderMobileCard?: (row: T) => ReactNode;
  footerSummary: ReactNode;
}

/**
 * One card per row: a headline, a subdued meta line, the remaining columns as
 * label/value pairs, and whatever the row's action column held pinned to the
 * bottom. It is the same data the table shows, re-flowed — no column is dropped
 * unless a screen said `mobile: 'hidden'`, because a value a receptionist needs
 * does not stop being needed on a phone.
 */
function MobileRows<T>({
  rows,
  isLoading,
  skeletonRows,
  emptyMessage,
  selectable,
  selection,
  onToggleRow,
  onRowClick,
  rowClassName,
  headerLabels,
  roleOf,
  renderMobileCard,
  footerSummary,
}: MobileRowsProps<T>) {
  const { t } = useTranslation('common');
  if (isLoading) {
    return (
      <div>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          <div key={`skeleton-${index}`} className="mobile-record-card gap-2">
            <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div data-testid="table-empty" className="flex flex-col items-center gap-2 p-8 text-slate-400">
        <Inbox className="h-8 w-8" strokeWidth={1.5} />
        <span className="text-xs">{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div>
      {rows.map((row) => {
        const isSelected = selectable && selection.has(row.id);
        const custom = renderMobileCard?.(row.original);

        const cells = row.getVisibleCells();
        const titleCell = cells.find((cell) => roleOf(cell) === 'title');
        const metaCells = cells.filter((cell) => roleOf(cell) === 'meta');
        const bodyCells = cells.filter((cell) => roleOf(cell) === 'row');
        const actionCells = cells.filter((cell) => roleOf(cell) === 'actions');

        return (
          <div
            key={row.id}
            data-testid="table-row"
            data-row-id={row.id}
            data-selected={isSelected || undefined}
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            onKeyDown={
              onRowClick
                ? (event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(row.original);
                    }
                  }
                : undefined
            }
            className={cn(
              'mobile-record-card',
              onRowClick && 'cursor-pointer active:bg-slate-50',
              isSelected && 'bg-blue-50',
              rowClassName?.(row.original),
            )}
          >
            {custom ?? (
              <>
                <div className="flex items-start gap-2">
                  {selectable ? (
                    // Stops the tap from opening the row it was meant to tick.
                    <span className="pt-0.5" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={selection.has(row.id)}
                        onChange={() => onToggleRow(row.id)}
                        aria-label={t('a11y.selectRow')}
                      />
                    </span>
                  ) : null}

                  <div className="min-w-0 flex-1">
                    {titleCell ? (
                      <div className="truncate text-sm font-bold text-slate-800">
                        {flexRender(titleCell.column.columnDef.cell, titleCell.getContext())}
                      </div>
                    ) : null}
                    {metaCells.length > 0 ? (
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                        {metaCells.map((cell) => (
                          <span key={cell.id} className="min-w-0 truncate">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* DS-50 — forward points the way the document reads. */}
                  {onRowClick ? (
                    <ChevronRight className="flip-rtl mt-0.5 h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                  ) : null}
                </div>

                {bodyCells.map((cell) => {
                  const numeric = cell.column.columnDef.meta?.numeric === true;
                  const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                  return (
                    <div key={cell.id} className="mobile-record-row">
                      <span className="mobile-record-label">{headerLabels.get(cell.column.id)}</span>
                      <span
                        className={cn(
                          'mobile-record-value',
                          // DS-23/24 — a figure keeps its mono weight and its red.
                          numeric && 'font-mono font-bold',
                          numeric && isNegative(cell.getValue()) && 'text-red-500',
                          cell.column.columnDef.meta?.cellClassName,
                        )}
                      >
                        {/* DS-51 — the figure itself stays LTR inside the Arabic label. */}
                        {numeric ? <span className="ltr-num">{rendered}</span> : rendered}
                      </span>
                    </div>
                  );
                })}

                {actionCells.length > 0 ? (
                  <div
                    className="-mb-1 flex flex-wrap items-center justify-end gap-1 border-t border-slate-100 pt-1.5"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {actionCells.map((cell) => (
                      <span key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </span>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        );
      })}

      {footerSummary}
    </div>
  );
}

interface MobileFooterProps<T> {
  table: Table<T>;
  headerLabels: Map<string, ReactNode>;
  hasColumnFooters: boolean;
  footerSummary: ReactNode;
}

/**
 * The totals, as a final card. A `<tfoot>` aligns its figures to columns that
 * no longer exist on a phone, so each one is re-labelled with the header of the
 * column it was totalling — otherwise a screen full of bare numbers.
 */
function MobileFooter<T>({ table, headerLabels, hasColumnFooters, footerSummary }: MobileFooterProps<T>) {
  return (
    <div className="sticky bottom-0 border-t border-slate-200 bg-slate-50 p-3">
      {hasColumnFooters
        ? table.getFooterGroups().map((footerGroup) =>
            footerGroup.headers.map((header) => {
              if (header.isPlaceholder || header.column.columnDef.footer === undefined) return null;
              return (
                <div key={header.id} className="mobile-record-row">
                  <span className="mobile-record-label">{headerLabels.get(header.column.id)}</span>
                  <span className="ltr-num mobile-record-value font-mono font-bold text-blue-600">
                    {flexRender(header.column.columnDef.footer, header.getContext())}
                  </span>
                </div>
              );
            }),
          )
        : null}
      {footerSummary ? (
        <div className="font-mono font-bold text-blue-600">{footerSummary}</div>
      ) : null}
    </div>
  );
}

/**
 * `forwardRef` erases the generic, so the ref-forwarding component is re-asserted
 * with it — callers keep `DataTable<Patient>` inference on `columns`/`data`.
 */
export const DataTable = forwardRef(DataTableImpl) as <T>(
  props: DataTableProps<T> & RefAttributes<HTMLDivElement>,
) => ReactElement;
